import type { MobileForensicNodeKind, ForensicTreeNode } from '@tranquility-ai/theme-components';
import type {
  UfdrChatMessage,
  UfdrChatSummary,
  UfdrContact,
  UfdrDeviceInfo,
  UfdrExportData,
  UfdrMediaItem,
  UfdrTimelineEvent,
} from '@/shared/ufdr/legacy-types';
import type { FileSummary, TreeNodeResponse } from './cors-provenance-api';
import { fileInfoFromProvenanceBody, getMediaType } from './functions';
import { treeNodeMeta } from './provenance-tree-ui';
import type { Source } from './types';

/**
 * Sidecar filenames produced by Cellebrite UFDR processing
 * (`mobile-forensics` staging uploads under ``files/<md5>/…``) plus Manual-folder
 * markdown siblings seen next to root ``.ufdr`` files (``ufdr_processing_messages.md``,
 * ``ufdr_calllog.md``).
 */
export type UfdrSidecarKind =
  | 'messagesJson'
  | 'contactsCsv'
  | 'deviceSelectorsCsv'
  | 'summaryMd'
  | 'callLog';

const MESSAGES_JSON = new Set(['messages.json']);
const CONTACTS_CSV = new Set(['contacts.csv']);
const DEVICE_SELECTORS_CSV = new Set(['device_selectors.csv']);
const SUMMARY_MD = new Set(['summary.md']);
const CALL_LOG = new Set([
  'ufdr_calllog.md',
  'call_log.json',
  'calllog.json',
  'call_log.csv',
  'ufdr_calllog.json',
]);
const MESSAGES_MD = new Set(['messages.md', 'ufdr_processing_messages.md']);

export function sidecarKindForName(name: string): UfdrSidecarKind | null {
  const base = name.split('/').pop()?.trim().toLowerCase() ?? '';
  if (!base) return null;
  if (MESSAGES_JSON.has(base)) return 'messagesJson';
  if (CONTACTS_CSV.has(base)) return 'contactsCsv';
  if (DEVICE_SELECTORS_CSV.has(base)) return 'deviceSelectorsCsv';
  if (SUMMARY_MD.has(base)) return 'summaryMd';
  if (CALL_LOG.has(base) || MESSAGES_MD.has(base)) return 'callLog';
  return null;
}

export function nodeFileUrl(node: TreeNodeResponse): string | undefined {
  const meta = treeNodeMeta(node);
  return typeof meta.fileUrl === 'string' && meta.fileUrl.trim() ? meta.fileUrl : undefined;
}

function nodeStorageUrls(node: TreeNodeResponse): {
  fileUrl?: string;
  previewUrl?: string;
  downloadUrl?: string;
} {
  const info = node.fileInfo;
  if (info?.kind === 'file') {
    return {
      fileUrl: info.file.fileUrl,
      previewUrl: info.file.previewUrl ?? undefined,
      downloadUrl: info.file.downloadUrl ?? undefined,
    };
  }
  if (info?.kind === 'subdocument') {
    return {
      fileUrl: info.subdocument.fileUrl,
      previewUrl: info.subdocument.previewUrl ?? undefined,
      downloadUrl: info.subdocument.downloadUrl ?? undefined,
    };
  }
  return { fileUrl: nodeFileUrl(node) };
}

/**
 * URLs to try when reading a leaf through ``/api/proxy``.
 *
 * ``files/<md5>/mobile_processing/…`` keys are often denied by the edge lambda.
 * For those, try the provenance ``previewUrl`` and ``downloadUrl`` first.
 */
export function nodeContentUrls(node: TreeNodeResponse): string[] {
  const { fileUrl, previewUrl, downloadUrl } = nodeStorageUrls(node);
  const mobileProcessing = (fileUrl ?? '').includes('mobile_processing');
  const ordered = mobileProcessing
    ? [previewUrl, downloadUrl, fileUrl]
    : [fileUrl, previewUrl, downloadUrl];
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const url of ordered) {
    const trimmed = url?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    urls.push(trimmed);
  }
  return urls;
}

/**
 * ``GET /tree?fileStatusId=&path=`` often returns children whose ``path`` is
 * only the basename (or a suffix relative to the requested folder). Prefix
 * with the parent listing path so ``buildUfdrTreeFromProvenance`` nests them
 * instead of promoting every leaf to the tree root.
 */
export function prefixChildTreePath(
  parentPath: string | undefined,
  node: TreeNodeResponse,
): TreeNodeResponse {
  const parent = (parentPath ?? '').replace(/^\/+|\/+$/g, '');
  if (!parent) return node;
  const raw = (node.path || node.name || '').replace(/^\/+|\/+$/g, '');
  if (!raw) return node;
  if (raw === parent || raw.startsWith(`${parent}/`)) return node;
  return { ...node, path: `${parent}/${raw}` };
}

const DEFAULT_MAX_DIR_FETCHES = 80;
const DEFAULT_MAX_NODES = 8000;

/** Every sidecar the export assembler knows how to read. */
export const UFDR_SIDECAR_KINDS: UfdrSidecarKind[] = [
  'messagesJson',
  'contactsCsv',
  'deviceSelectorsCsv',
  'summaryMd',
  'callLog',
];

/**
 * ``collected/<32-hex>`` directories. A UFDR lists thousands of these and they
 * crowd out the folders that actually hold ``messages.json`` and ``contacts.csv``.
 */
export function isCollectedHashFolder(path: string): boolean {
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) return false;
  const parent = segments[segments.length - 2];
  const name = segments[segments.length - 1] ?? '';
  return parent === 'collected' && /^[0-9a-f]{32}$/i.test(name);
}

/**
 * Breadth-first walk of ``GET /tree?fileStatusId=&path=`` listings. Each
 * directory is fetched (not just the first level) so PDFs and other leaves
 * under ``collected/files`` and ``pdf/communication`` appear as tree children.
 *
 * ``collected/<md5>`` folders are deferred until every other folder has been
 * listed. ``shouldStop`` only skips those hash folders. It does not abandon
 * the remaining non-hash queue: sidecar files often show up before
 * ``generated/mobile/threads`` and ``pdf/communication``.
 */
export async function collectNestedUfdrTreeNodes(args: {
  listDirectory: (path: string | undefined) => (Promise<TreeNodeResponse[]>);
  maxDirFetches?: number;
  maxNodes?: number;
  /** Fired after each directory is merged, so sidecar downloads can start early. */
  onProgress?: (nodes: TreeNodeResponse[]) => void;
  /**
   * Skip the deferred hash folders once this returns true. The non-hash walk
   * still finishes.
   */
  shouldStop?: (nodes: TreeNodeResponse[]) => boolean;
  /** First directory to list. Omit it to start at the archive root. */
  rootPath?: string;
  /**
   * ``collected/<md5>`` directories left unlisted because the walk stopped or
   * hit its fetch cap. The host can page these after first paint.
   */
  deferredPaths?: string[];
}): (Promise<TreeNodeResponse[]>) {
  const maxDirFetches = args.maxDirFetches ?? DEFAULT_MAX_DIR_FETCHES;
  const maxNodes = args.maxNodes ?? DEFAULT_MAX_NODES;
  const collected: TreeNodeResponse[] = [];
  const seen = new Set<string>();
  const priority: Array<string | undefined> = [args.rootPath];
  const deferred: string[] = [];
  let dirFetches = 0;
  let stopped = false;

  const enqueue = (path: string) => {
    if (isCollectedHashFolder(path)) deferred.push(path);
    else priority.push(path);
  };

  while (
    (priority.length > 0 || (!stopped && deferred.length > 0)) &&
    dirFetches < maxDirFetches &&
    collected.length < maxNodes
  ) {
    const parentPath = priority.length > 0 ? priority.shift() : deferred.shift();
    dirFetches += 1;
    const items = await args.listDirectory(parentPath);
    for (const item of items) {
      if (collected.length >= maxNodes) break;
      const prefixed = prefixChildTreePath(parentPath, item);
      const key = `${prefixed.nodeType}:${prefixed.path}`;
      if (seen.has(key)) continue;
      seen.add(key);
      collected.push(prefixed);
      if (prefixed.nodeType === 'directory' && prefixed.path) {
        enqueue(prefixed.path);
      }
    }
    args.onProgress?.(collected);
    if (args.shouldStop?.(collected)) stopped = true;
  }

  if (deferred.length > 0) args.deferredPaths?.push(...deferred);
  return collected;
}

function nodeKind(node: TreeNodeResponse): MobileForensicNodeKind {
  if (node.nodeType === 'directory') return 'folder';
  const name = (node.name || node.path || '').toLowerCase();
  if (name.endsWith('.xml') || name === 'report.xml') return 'report';
  if (/\.(db|sqlite|sqlite3)$/i.test(name)) return 'database';
  return 'file';
}

function nodeMediaFields(node: TreeNodeResponse): Pick<ForensicTreeNode, 'mediaType' | 'mediaSrc'> {
  const fileUrl = nodeFileUrl(node);
  if (!fileUrl || node.nodeType === 'directory') return {};
  const summary = node.fileInfo ? fileInfoFromProvenanceBody(node.fileInfo) : null;
  const mediaType = getMediaType({ sourceFilePath: fileUrl } as Source, summary);
  return {
    mediaType: mediaType && mediaType !== 'unknown' ? mediaType : undefined,
    mediaSrc: fileUrl,
  };
}

/**
 * Nest a flat provenance listing (``path`` segments) into ``ForensicTreeNode``s.
 * Directory rows become folders; files/subdocuments become leaves.
 */
export function buildUfdrTreeFromProvenance(nodes: TreeNodeResponse[]): ForensicTreeNode[] {
  const root: ForensicTreeNode[] = [];
  const folders = new Map<string, ForensicTreeNode>();

  const ensureFolder = (segments: string[]): ForensicTreeNode => {
    const key = segments.join('/');
    const existing = folders.get(key);
    if (existing) return existing;
    const node: ForensicTreeNode = {
      id: `folder:${key}`,
      label: segments[segments.length - 1] || key,
      path: key,
      kind: 'folder',
      children: [],
      collected: isCollectedHashFolder(key) || undefined,
    };
    folders.set(key, node);
    if (segments.length === 1) {
      root.push(node);
    } else {
      const parent = ensureFolder(segments.slice(0, -1));
      parent.children = parent.children ?? [];
      parent.children.push(node);
    }
    return node;
  };

  for (const node of nodes) {
    const segments = (node.path || node.name || '').split('/').filter(Boolean);
    if (segments.length === 0) continue;
    if (node.nodeType === 'directory') {
      const folder = ensureFolder(segments);
      folder.badge = node.hasChildren ? undefined : folder.badge;
      continue;
    }
    const leaf: ForensicTreeNode = {
      id: node.id || node.path || node.name,
      label: node.name || segments[segments.length - 1] || node.path,
      path: node.path,
      kind: nodeKind(node),
      ...nodeMediaFields(node),
    };
    if (segments.length === 1) {
      root.push(leaf);
    } else {
      const parent = ensureFolder(segments.slice(0, -1));
      parent.children = parent.children ?? [];
      parent.children.push(leaf);
    }
  }

  const decorateBadges = (items: ForensicTreeNode[]) => {
    for (const item of items) {
      if (item.kind === 'folder' && item.children && item.children.length > 0) {
        item.badge = item.children.length;
        decorateBadges(item.children);
      }
    }
  };
  decorateBadges(root);
  return root;
}

export function collectUfdrMediaFromTree(tree: ForensicTreeNode[]): UfdrMediaItem[] {
  const media: UfdrMediaItem[] = [];
  const walk = (nodes: ForensicTreeNode[]) => {
    for (const node of nodes) {
      const isSidecar = sidecarKindForName(node.label || node.path || '');
      if (node.mediaSrc && node.kind !== 'folder' && !chatThreadParts(node.path) && !isSidecar) {
        media.push({
          id: node.id,
          label: node.label,
          mediaType: node.mediaType,
          src: node.mediaSrc,
          path: node.path,
        });
      }
      if (node.children) walk(node.children);
    }
  };
  walk(tree);
  return media;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function isDeviceUser(sender: string | undefined): boolean {
  if (!sender) return false;
  return /^device user\b/i.test(sender.trim());
}

function attachmentType(contentType: string | undefined, key: string): 'image' | 'video' | 'link' {
  const mime = (contentType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  const ext = key.split('.').pop()?.toLowerCase() ?? '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'm4v', 'webm'].includes(ext)) return 'video';
  return 'link';
}

export function mapMessagesJsonToChats(payload: unknown): UfdrChatSummary[] {
  const platforms = Array.isArray(payload) ? payload : [];
  const chats: UfdrChatSummary[] = [];

  for (const platform of platforms) {
    if (!isRecord(platform)) continue;
    const app = asString(platform.platform_name) || asString(platform.platform) || 'Messages';
    const threads = Array.isArray(platform.threads) ? platform.threads : [];
    for (const thread of threads) {
      if (!isRecord(thread)) continue;
      const participants = Array.isArray(thread.participants)
        ? thread.participants.map((p) => asString(p)).filter((p): p is string => !!p)
        : [];
      const peer =
        participants.find((p) => !isDeviceUser(p)) ||
        participants[0] ||
        'Unknown';
      const rawMessages = Array.isArray(thread.messages) ? thread.messages : [];
      const messages: UfdrChatMessage[] = rawMessages.flatMap((raw, index) => {
        if (!isRecord(raw)) return [];
        const sender = asString(raw.sender);
        const parts = Array.isArray(raw.parts) ? raw.parts : [];
        const textParts: string[] = [];
        const attachments: UfdrChatMessage['attachments'] = [];
        for (const part of parts) {
          if (typeof part === 'string') {
            if (part.trim()) textParts.push(part);
            continue;
          }
          if (!isRecord(part)) continue;
          const key = asString(part.key) || '';
          const url = asString(part.s3_url) || asString(part.url);
          if (url) {
            attachments.push({
              type: attachmentType(asString(part.content_type), key),
              url,
              alt: key || undefined,
            });
          } else if (key) {
            textParts.push(key);
          }
        }
        const id =
          asString(raw.message_uuid) ||
          asString(raw.id) ||
          `${asString(thread.thread_uuid) || 'thread'}-${index}`;
        return [
          {
            id,
            direction: isDeviceUser(sender) ? 'outgoing' : 'incoming',
            text: textParts.join('\n') || undefined,
            timestamp: asString(raw.time),
            sender,
            attachments: attachments.length > 0 ? attachments : undefined,
          },
        ];
      });
      const last = messages[messages.length - 1];
      chats.push({
        id: asString(thread.thread_uuid) || `${app}-${peer}-${chats.length}`,
        app,
        peer,
        participants: participants.length > 0 ? participants : undefined,
        lastMessage: last?.text,
        timestamp: last?.timestamp,
        messageCount: messages.length,
        messages: messages.length > 0 ? messages : undefined,
      });
    }
  }

  return chats;
}

function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const split = (line: string) =>
    line.split(',').map((cell) => cell.trim().replace(/^"|"$/g, ''));
  const headers = split(lines[0]!).map((h) => h.toLowerCase());
  const rows = lines.slice(1).map(split);
  return { headers, rows };
}

function csvCol(headers: string[], row: string[], name: string): string | undefined {
  const index = headers.indexOf(name);
  if (index < 0) return undefined;
  const value = row[index]?.trim();
  return value || undefined;
}

export function mapContactsCsvToContacts(csv: string): UfdrContact[] {
  const { headers, rows } = parseCsv(csv);
  return rows.flatMap((row, index) => {
    const name = csvCol(headers, row, 'contact_name') || csvCol(headers, row, 'name');
    const phone = csvCol(headers, row, 'phone_number') || csvCol(headers, row, 'phone');
    const email = csvCol(headers, row, 'email');
    if (!name && !phone && !email) return [];
    return [
      {
        id: `contact-${index}-${name || phone || email}`,
        name: name || phone || email || 'Unknown',
        phoneNumbers: phone ? [phone] : undefined,
        emails: email ? [email] : undefined,
      },
    ];
  });
}

export function mapDeviceSelectorsCsvToDevice(csv: string): UfdrDeviceInfo | undefined {
  const { headers, rows } = parseCsv(csv);
  const row = rows[0];
  if (!row) return undefined;
  const device: UfdrDeviceInfo = {
    imei: csvCol(headers, row, 'imei'),
    phoneNumber: csvCol(headers, row, 'phone_number'),
  };
  if (!device.imei && !device.phoneNumber) return undefined;
  return device;
}

const TIMESTAMP_LINE =
  /^(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:?\d{2})?)\s+(.+)$/;

export function mapCallLogToTimeline(payload: string, filename: string): UfdrTimelineEvent[] {
  const trimmed = payload.trim();
  if (!trimmed) return [];

  if (filename.toLowerCase().endsWith('.json')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      const items = Array.isArray(parsed) ? parsed : [];
      return items.flatMap((item, index) => {
        if (!isRecord(item)) return [];
        const timestamp =
          asString(item.timestamp) ||
          asString(item.time) ||
          asString(item.start_date) ||
          asString(item.time_stamp);
        const description =
          asString(item.description) ||
          asString(item.subject) ||
          asString(item.details) ||
          asString(item.direction);
        if (!timestamp && !description) return [];
        return [
          {
            id: asString(item.id) || `call-${index}`,
            timestamp: timestamp || '',
            description: description || 'Call log entry',
            category: asString(item.category) || 'call',
          },
        ];
      });
    } catch {
      return [];
    }
  }

  const events: UfdrTimelineEvent[] = [];
  for (const line of trimmed.split(/\r?\n/)) {
    const match = line.trim().match(TIMESTAMP_LINE);
    if (!match) continue;
    events.push({
      id: `call-${events.length}`,
      timestamp: match[1]!,
      description: match[2]!.trim(),
      category: 'call',
    });
  }
  return events;
}

function timelineFromChats(chats: UfdrChatSummary[]): UfdrTimelineEvent[] {
  return chats.flatMap((chat) =>
    (chat.messages ?? [])
      .filter((message) => message.timestamp)
      .map((message) => ({
        id: `chat-${message.id}`,
        timestamp: message.timestamp as string,
        description: message.text
          ? `${chat.app}: ${message.text}`
          : `${chat.app} message with ${chat.peer}`,
        category: 'message',
      })),
  );
}

export function deviceFromFileInfo(fileInfo?: FileSummary | null): UfdrDeviceInfo | undefined {
  if (!fileInfo) return undefined;
  const model = fileInfo.fileName?.replace(/\.(ufdr|zip)$/i, '').replace(/[_-]+/g, ' ').trim();
  if (!model) return undefined;
  return { model };
}

function phoneDigits(value: string | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  return digits.length >= 7 ? digits : '';
}

function contactTouchesChat(contact: UfdrContact, chat: UfdrChatSummary): boolean {
  const chatIds = (contact as UfdrContact & { chatIds?: string[] }).chatIds;
  if (chatIds?.includes(chat.id)) return true;
  const names = new Set(
    [contact.name, ...(contact.phoneNumbers ?? []), ...(contact.emails ?? [])]
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean),
  );
  const phones = new Set((contact.phoneNumbers ?? []).map(phoneDigits).filter(Boolean));
  const peers = [chat.peer, ...(chat.participants ?? [])];
  return peers.some((peer) => {
    const normalized = peer.trim().toLowerCase();
    if (names.has(normalized)) return true;
    const digits = phoneDigits(peer);
    if (!digits) return false;
    return [...phones].some((phone) => phone.endsWith(digits) || digits.endsWith(phone));
  });
}

export function withChatIds(contacts: UfdrContact[], chats: UfdrChatSummary[]): UfdrContact[] {
  return contacts.map((contact) => {
    const chatIds = chats.filter((chat) => contactTouchesChat(contact, chat)).map((chat) => chat.id);
    return chatIds.length > 0 ? ({ ...contact, chatIds } as UfdrContact) : contact;
  });
}

/** Last resort when ``contacts.csv`` and the mobile-service artifact are empty. */
export function contactsFromChatParticipants(chats: UfdrChatSummary[]): UfdrContact[] {
  const byKey = new Map<string, UfdrContact & { chatIds: string[] }>();
  for (const chat of chats) {
    const named = (chat.participants ?? []).filter((name) => !isDeviceUser(name));
    const people = named.length > 0 ? named : isDeviceUser(chat.peer) || /^thread /i.test(chat.peer) ? [] : [chat.peer];
    for (const person of people) {
      const phone = person.match(/(\+?\d[\d\s().-]{6,}\d)/)?.[1]?.trim();
      const name = person.replace(/\([^)]*\)/g, '').trim() || person;
      const key = phoneDigits(phone) || name.toLowerCase();
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.chatIds.includes(chat.id)) existing.chatIds.push(chat.id);
        if (!existing.phoneNumbers && phone) existing.phoneNumbers = [phone];
        continue;
      }
      byKey.set(key, {
        id: `contact-${key}`,
        name,
        phoneNumbers: phone ? [phone] : undefined,
        chatIds: [chat.id],
        source: 'messages',
      });
    }
  }
  return [...byKey.values()].map((contact) => ({ ...contact }) as UfdrContact);
}

export function mergeUfdrCollectedTree(
  base: UfdrExportData,
  extraNodes: TreeNodeResponse[],
): UfdrExportData {
  if (extraNodes.length === 0) return base;
  const extraTree = buildUfdrTreeFromProvenance(extraNodes);
  const merge = (left: ForensicTreeNode[], right: ForensicTreeNode[]): ForensicTreeNode[] => {
    const out: ForensicTreeNode[] = left.map((node) => ({
      ...node,
      children: node.children ? [...node.children] : undefined,
    }));
    for (const node of right) {
      const existing = out.find((item) => item.id === node.id || item.path === node.path || item.label === node.label);
      if (!existing) {
        out.push(node);
        continue;
      }
      if (node.children && node.children.length > 0) {
        existing.children = merge(existing.children ?? [], node.children);
        existing.badge = existing.children.length;
      }
    }
    return out;
  };
  const tree = merge(base.tree ?? [], extraTree);
  const media = collectUfdrMediaFromTree(tree);
  return {
    ...base,
    tree,
    media: media.length > 0 ? media : base.media,
  };
}

export function assembleUfdrExportData(args: {
  title?: string;
  nodes: TreeNodeResponse[];
  fileInfo?: FileSummary | null;
  sidecars?: Partial<Record<UfdrSidecarKind, string>>;
  /** Basename used to decide whether the call log is JSON or markdown. */
  callLogFilename?: string;
  /** Downloaded ``chat-thread-*.md`` bodies, keyed by the node path. */
  chatThreadTexts?: Record<string, string>;
}): UfdrExportData {
  const tree = buildUfdrTreeFromProvenance(args.nodes);
  const sidecars = args.sidecars ?? {};
  let chats: UfdrChatSummary[] | undefined;
  if (sidecars.messagesJson) {
    try {
      chats = mapMessagesJsonToChats(JSON.parse(sidecars.messagesJson) as unknown);
    } catch {
      chats = undefined;
    }
  }
  if (!chats || chats.length === 0) {
    const fromThreads = mapChatThreadNodes(args.nodes, args.chatThreadTexts);
    chats = fromThreads.length > 0 ? fromThreads : undefined;
  }
  const csvContacts = sidecars.contactsCsv
    ? mapContactsCsvToContacts(sidecars.contactsCsv)
    : undefined;
  const contacts =
    csvContacts && csvContacts.length > 0
      ? withChatIds(csvContacts, chats ?? [])
      : chats && chats.length > 0
        ? contactsFromChatParticipants(chats)
        : undefined;
  const selectorDevice = sidecars.deviceSelectorsCsv
    ? mapDeviceSelectorsCsvToDevice(sidecars.deviceSelectorsCsv)
    : undefined;
  const fileDevice = deviceFromFileInfo(args.fileInfo);
  const device =
    selectorDevice || fileDevice
      ? { ...fileDevice, ...selectorDevice }
      : undefined;
  const media = collectUfdrMediaFromTree(tree);
  const callTimeline = sidecars.callLog
    ? mapCallLogToTimeline(sidecars.callLog, args.callLogFilename || 'ufdr_calllog.md')
    : [];
  const chatTimeline = chats ? timelineFromChats(chats) : [];
  const timeline = [...callTimeline, ...chatTimeline].sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );

  return {
    title: args.title,
    device,
    tree,
    chats,
    contacts: contacts && contacts.length > 0 ? contacts : undefined,
    media: media.length > 0 ? media : undefined,
    timeline: timeline.length > 0 ? timeline : undefined,
  };
}

export function resolveUfdrOpenMediaUrl(
  item: ForensicTreeNode | UfdrMediaItem,
): string | undefined {
  if ('mediaSrc' in item && typeof item.mediaSrc === 'string' && item.mediaSrc.trim()) {
    return item.mediaSrc;
  }
  if ('src' in item && typeof item.src === 'string' && item.src.trim()) {
    return item.src;
  }
  return undefined;
}

/** Tree leaves and media tiles that should open the in-modal PDF viewer. */
export function ufdrItemIsPdf(item: ForensicTreeNode | UfdrMediaItem): boolean {
  const mediaType = 'mediaType' in item ? item.mediaType?.toLowerCase() : undefined;
  if (mediaType === 'pdf') return true;
  const haystack = [
    'label' in item ? item.label : '',
    'path' in item ? item.path : '',
    resolveUfdrOpenMediaUrl(item),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ');
  return /\.pdf(?:$|[?#])/i.test(haystack);
}

const CHAT_APP_BY_KEY: Record<string, { app: string; platform: string }> = {
  whatsapp: { app: 'WhatsApp', platform: 'whatsapp' },
  signal: { app: 'Signal', platform: 'signal' },
  telegram: { app: 'Telegram', platform: 'telegram' },
  imessage: { app: 'SMS / iMessage', platform: 'smsIos' },
  messages: { app: 'SMS / iMessage', platform: 'smsIos' },
  sms: { app: 'SMS / RCS', platform: 'smsAndroid' },
  mms: { app: 'SMS / RCS', platform: 'smsAndroid' },
  rcs: { app: 'SMS / RCS', platform: 'smsAndroid' },
  messenger: { app: 'Messenger', platform: 'messenger' },
  facebookmessenger: { app: 'Messenger', platform: 'messenger' },
  threema: { app: 'Threema', platform: 'threema' },
  viber: { app: 'Viber', platform: 'viber' },
  line: { app: 'LINE', platform: 'line' },
  kakaotalk: { app: 'KakaoTalk', platform: 'kakaotalk' },
  wechat: { app: 'WeChat Premium', platform: 'wechat' },
  discord: { app: 'Discord', platform: 'discord' },
  slack: { app: 'Slack', platform: 'slack' },
  teams: { app: 'Microsoft Teams', platform: 'teams' },
  microsoftteams: { app: 'Microsoft Teams', platform: 'teams' },
  googlechat: { app: 'Google Chat', platform: 'googleChat' },
  snapchat: { app: 'Snapchat', platform: 'snapchat' },
  instagram: { app: 'Instagram DM', platform: 'instagramDm' },
  instagramdirect: { app: 'Instagram DM', platform: 'instagramDm' },
};

function knownChatApp(raw: string | undefined): { app: string; platform: string } | undefined {
  if (!raw) return undefined;
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  return CHAT_APP_BY_KEY[key];
}

/** Folder name, or a ``**Platform**`` header in the thread markdown. */
export function resolveThreadApp(
  folderApp: string,
  text?: string | null,
): { app: string; platform?: string } {
  const header = text?.match(/^\*\*(?:platform|app|source)\*\*:\s*(.+)$/im)?.[1]?.trim();
  return knownChatApp(header) ?? knownChatApp(folderApp) ?? { app: folderApp };
}

const CHAT_THREAD_PATH = /(?:^|\/)threads\/([^/]+)\/(chat-thread-([^/]+))\.md$/i;

/** ``generated/mobile/threads/<app>/chat-thread-*.md`` leaves from UFDR processing. */
export function chatThreadParts(
  path: string | undefined | null,
): { app: string; threadId: string } | null {
  if (!path) return null;
  const match = path.match(CHAT_THREAD_PATH);
  if (!match) return null;
  const app = (match[1] ?? '').replace(/[._]+/g, ' ').trim();
  const threadId = (match[3] ?? '').trim();
  if (!app || !threadId) return null;
  return { app, threadId };
}

/**
 * One processing thread file. Bold fields (``**Message**``, ``**Sender**``,
 * ``**Participants**``) become messages when the body was downloaded. The chat
 * is still listed when the body is missing, so the Chats count is not zero
 * just because the file proxy has not returned yet.
 */
export function mapChatThreadFile(args: {
  path: string;
  text?: string | null;
}): UfdrChatSummary | null {
  const parts = chatThreadParts(args.path);
  if (!parts) return null;
  const messages: UfdrChatMessage[] = [];
  let participants: string[] = [];
  let sender: string | undefined;
  let timestamp: string | undefined;
  const lines = (args.text ?? '').split(/\r?\n/);
  for (const line of lines) {
    const field = line.trim().match(/^\*\*([^*]+)\*\*:\s*(.*)$/);
    if (!field) continue;
    const name = field[1]!.trim().toLowerCase();
    const value = field[2]!.trim();
    if (!value) continue;
    if (name === 'participants' || name === 'participant') {
      participants = value.split(/[;,]/).map((part) => part.trim()).filter(Boolean);
      continue;
    }
    if (name === 'sender' || name === 'from' || name === 'author') {
      sender = value;
      continue;
    }
    if (name === 'time' || name === 'timestamp' || name === 'date') {
      timestamp = value;
      continue;
    }
    if (name === 'message' || name === 'body' || name === 'text') {
      messages.push({
        id: `${parts.threadId}-${messages.length}`,
        direction: isDeviceUser(sender) ? 'outgoing' : 'incoming',
        text: value,
        timestamp,
        sender,
      });
      sender = undefined;
      timestamp = undefined;
    }
  }
  const peer =
    participants.find((name) => !isDeviceUser(name)) ||
    messages.find((message) => message.sender && !isDeviceUser(message.sender))?.sender ||
    `Thread ${parts.threadId}`;
  const last = messages[messages.length - 1];
  const app = resolveThreadApp(parts.app, args.text);
  return {
    id: args.path,
    app: app.app,
    platform: app.platform,
    peer,
    participants: participants.length > 0 ? participants : undefined,
    lastMessage: last?.text,
    timestamp: last?.timestamp,
    messageCount: messages.length > 0 ? messages.length : undefined,
    messages: messages.length > 0 ? messages : undefined,
    ...(messages.length === 0 ? { loadable: true } : {}),
  };
}

export function mapChatThreadNodes(
  nodes: TreeNodeResponse[],
  texts?: Record<string, string>,
): UfdrChatSummary[] {
  const chats: UfdrChatSummary[] = [];
  for (const node of nodes) {
    const path = node.path || node.name;
    if (!path || node.nodeType === 'directory') continue;
    const chat = mapChatThreadFile({ path, text: texts?.[path] });
    if (chat) chats.push(chat);
  }
  return chats;
}

export function findSidecarNodes(
  nodes: TreeNodeResponse[],
): Partial<Record<UfdrSidecarKind, TreeNodeResponse>> {
  const found: Partial<Record<UfdrSidecarKind, TreeNodeResponse>> = {};
  for (const node of nodes) {
    const kind = sidecarKindForName(node.name || node.path);
    if (!kind || found[kind]) continue;
    found[kind] = node;
  }
  return found;
}
